import { useState, useEffect, useCallback } from 'react';
import { paymentsAPI, patientDashboardAPI, servicesAPI, patientsAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';
import {
  CreditCard, Search, Plus, Receipt, User, Calendar, IndianRupee,
  CheckCircle2, Printer, Filter, History, X, AlertCircle,
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { cn } from '../lib/utils';
import { printReceiptHTML } from './InvoicePrint';
import { Link } from 'react-router-dom';

const METHOD_OPTIONS = [
  { v: 'cash', l: 'Cash' },
  { v: 'upi', l: 'UPI' },
  { v: 'card', l: 'Card' },
  { v: 'bank_transfer', l: 'Bank Transfer' },
  { v: 'netbanking', l: 'Net Banking' },
  { v: 'cheque', l: 'Cheque' },
  { v: 'wallet', l: 'Wallet' },
  { v: 'insurance', l: 'Insurance' },
];

const PaymentsPage = () => {
  const { hospital } = useAuth();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCollect, setShowCollect] = useState(false);
  const [lastReceipt, setLastReceipt] = useState(null);
  const [filter, setFilter] = useState('all');

  const loadPayments = useCallback(async () => {
    try {
      const params = {};
      if (filter !== 'all') params.status = filter;
      const res = await paymentsAPI.list(params);
      setPayments(res.data || []);
    } catch { toast.error('Failed to load payments'); }
    setLoading(false);
  }, [filter]);

  useEffect(() => { loadPayments(); }, [loadPayments]);

  // v37b2 — show NET totals (after approved refunds) so dashboard reconciles with InvoiceHistory.
  const totalCollected = payments.filter(p => p.status === 'completed').reduce((s, p) => s + (p.net_amount != null ? p.net_amount : (p.amount || 0)), 0);
  const todayPayments = payments.filter(p => p.created_at?.startsWith(new Date().toISOString().slice(0, 10)));
  const todayTotal = todayPayments.reduce((s, p) => s + (p.net_amount != null ? p.net_amount : (p.amount || 0)), 0);

  const handleReprint = async (p) => {
    try {
      const patientRes = await patientsAPI.get(p.patient_id);
      printReceiptHTML({ receipt: p, patient: patientRes.data, hospital: hospital || {} });
    } catch {
      // fall back with whatever metadata is on the row
      printReceiptHTML({ receipt: p, patient: { first_name: p.patient_name, patient_id: p.patient_uhid }, hospital: hospital || {} });
    }
  };

  return (
    <div className="space-y-6" data-testid="payments-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Payment Collection</h1>
          <p className="text-sm text-slate-500 mt-0.5">Collect and track patient payments — split-method ready, insurance-grade receipts</p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/invoice-history">
            <Button variant="outline" className="gap-2" data-testid="invoice-history-link">
              <History size={14} /> Invoice History
            </Button>
          </Link>
          <Button onClick={() => setShowCollect(true)} className="gap-2 bg-teal-600 hover:bg-teal-700" data-testid="collect-payment-btn">
            <Plus size={16} /> Payment Collected
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-teal-50 flex items-center justify-center"><IndianRupee size={18} className="text-teal-600" /></div>
            <div>
              <p className="text-xs text-slate-500">Total Collected</p>
              <p className="text-xl font-bold text-slate-900" data-testid="total-collected">Rs. {totalCollected.toLocaleString()}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-emerald-50 flex items-center justify-center"><Calendar size={18} className="text-emerald-600" /></div>
            <div>
              <p className="text-xs text-slate-500">Today's Collection</p>
              <p className="text-xl font-bold text-slate-900">Rs. {todayTotal.toLocaleString()}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-blue-50 flex items-center justify-center"><Receipt size={18} className="text-blue-600" /></div>
            <div>
              <p className="text-xs text-slate-500">Total Receipts</p>
              <p className="text-xl font-bold text-slate-900">{payments.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-3">
        <Filter size={14} className="text-slate-400" />
        {['all', 'completed', 'pending', 'refunded'].map(f => (
          <button key={f} onClick={() => setFilter(f)}
            className={cn('text-xs px-3 py-1.5 rounded-full font-medium transition-colors capitalize',
              filter === f ? 'bg-teal-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            )} data-testid={`filter-${f}`}>{f}</button>
        ))}
      </div>

      {/* Payments Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Receipt #</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Patient</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Amount</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Method(s)</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Service / Description</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Status</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Date</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Collected By</th>
              <th className="px-2 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} className="text-center py-12 text-slate-400">Loading...</td></tr>
            ) : payments.length === 0 ? (
              <tr><td colSpan={9} className="text-center py-12 text-slate-400">No payments found</td></tr>
            ) : payments.map(p => {
              const methods = Array.isArray(p.splits) && p.splits.length
                ? p.splits.map(s => (s.method || '').toUpperCase()).join(' + ')
                : (p.payment_method || '').toUpperCase();
              return (
                <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`payment-row-${p.id}`}>
                  <td className="px-4 py-3 font-mono text-xs text-slate-700">{p.receipt_number}</td>
                  <td className="px-4 py-3">
                    <p className="text-xs font-medium text-slate-800">{p.patient_name || '-'}</p>
                    <p className="text-[10px] text-slate-400">{p.patient_uhid || ''}</p>
                  </td>
                  <td className="px-4 py-3 font-bold text-slate-800">
                    {(() => {
                      const orig = p.amount || 0;
                      const refunded = p.refunded_amount || 0;
                      const net = p.net_amount != null ? p.net_amount : orig;
                      if (refunded > 0) {
                        return (
                          <div className="space-y-0.5">
                            <p className={net === 0 ? 'line-through text-slate-400' : ''} data-testid={`pay-amount-${p.id}`}>
                              Rs. {net.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                            </p>
                            <p className="text-[10px] text-rose-600 font-semibold">− Rs. {refunded.toLocaleString(undefined, { maximumFractionDigits: 2 })} refunded</p>
                          </div>
                        );
                      }
                      return <span data-testid={`pay-amount-${p.id}`}>Rs. {orig.toLocaleString()}</span>;
                    })()}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600">{methods}</td>
                  <td className="px-4 py-3 text-xs text-slate-600 max-w-40 truncate">{p.service_name || p.description || '-'}</td>
                  <td className="px-4 py-3">
                    <span className={cn('text-[10px] px-2 py-0.5 rounded-full font-bold uppercase',
                      p.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                      p.status === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                    )}>{p.status}</span>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500">{p.created_at ? new Date(p.created_at).toLocaleDateString() : '-'}</td>
                  <td className="px-4 py-3 text-xs text-slate-500">{p.collected_by_name || '-'}</td>
                  <td className="px-2 py-3">
                    <button
                      onClick={() => window.open(`/invoice/print/${p.id}`, '_blank')}
                      title="Print Invoice"
                      className="p-1.5 rounded hover:bg-teal-50 text-emerald-600 hover:text-emerald-700"
                      data-testid={`reprint-${p.id}`}>
                      <Printer size={14} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Collect Payment Dialog */}
      {showCollect && (
        <CollectPaymentDialog
          onClose={() => setShowCollect(false)}
          onSuccess={(receipt) => { setShowCollect(false); setLastReceipt(receipt); loadPayments(); }}
        />
      )}

      {/* Receipt + Invoice + Print Dialog */}
      {lastReceipt && (
        <ReceiptPrintDialog receipt={lastReceipt} hospital={hospital || {}} onClose={() => setLastReceipt(null)} />
      )}
    </div>
  );
};

// ===================== Collect Payment Dialog =====================
const CollectPaymentDialog = ({ onClose, onSuccess }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);

  const [services, setServices] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [selectedServiceId, setSelectedServiceId] = useState('');
  const [description, setDescription] = useState('');

  const [baseAmount, setBaseAmount] = useState('');
  const [taxRate, setTaxRate] = useState(0); // % from service or 0

  // Item 5 — multi-consultation invoice (one receipt, multiple doctors/services as line items)
  const [multiConsult, setMultiConsult] = useState(false);
  const [items, setItems] = useState([
    { doctor_id: '', doctor_name: '', service_id: '', service_name: '', description: '', base_amount: '', tax_rate: 0 },
  ]);

  // splits: at least one row
  const [splits, setSplits] = useState([{ method: 'cash', amount: '', reference: '' }]);
  const [submitting, setSubmitting] = useState(false);

  // Load services + doctors for current hospital
  useEffect(() => {
    (async () => {
      try {
        const res = await servicesAPI.list();
        setServices((res.data || []).filter(s => s.is_active !== false));
      } catch { /* optional */ }
      try {
        const res = await patientsAPI.getDoctors();
        setDoctors(res.data || []);
      } catch { /* optional */ }
    })();
  }, []);

  const handleSearch = async (q) => {
    setSearchQuery(q);
    if (q.length < 2) { setSearchResults([]); return; }
    try {
      const res = await patientDashboardAPI.search(q);
      setSearchResults(res.data || []);
    } catch { /* ignore */ }
  };

  const handleServiceChange = (id) => {
    setSelectedServiceId(id);
    if (id === '__custom__') {
      setBaseAmount('');
      setTaxRate(0);
      setDescription('');
      return;
    }
    const svc = services.find(s => s.id === id);
    if (svc) {
      setBaseAmount(String(svc.price || 0));
      setTaxRate(Number(svc.tax_rate || 0));
      setDescription(svc.name || '');
    }
  };

  const updateSplit = (idx, key, val) => {
    setSplits(prev => prev.map((s, i) => i === idx ? { ...s, [key]: val } : s));
  };
  const addSplit = () => setSplits(prev => [...prev, { method: 'upi', amount: '', reference: '' }]);
  const removeSplit = (idx) => setSplits(prev => prev.length === 1 ? prev : prev.filter((_, i) => i !== idx));

  const selectedService = services.find(s => s.id === selectedServiceId);

  // Item 5 — items math (when multi-consultation toggled). When ON, base/tax/grand override above.
  const itemsTotals = items.map(it => {
    const b = parseFloat(it.base_amount) || 0;
    const t = +(b * ((parseFloat(it.tax_rate) || 0) / 100)).toFixed(2);
    return { ...it, _base: b, _tax: t, _amount: +(b + t).toFixed(2) };
  });
  const itemsBase = itemsTotals.reduce((s, it) => s + it._base, 0);
  const itemsTax = itemsTotals.reduce((s, it) => s + it._tax, 0);
  const itemsGrand = +(itemsBase + itemsTax).toFixed(2);

  // Effective totals to drive splits & submit (multi-consultation aware)
  const base = multiConsult ? itemsBase : (parseFloat(baseAmount) || 0);
  const taxAmount = multiConsult ? itemsTax : +(base * (taxRate / 100)).toFixed(2);
  const grandTotal = multiConsult ? itemsGrand : +(base + taxAmount).toFixed(2);
  const splitSum = splits.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
  const splitsMatch = Math.abs(splitSum - grandTotal) < 0.01 && grandTotal > 0;

  const autoFillRemainder = (idx) => {
    const others = splits.reduce((s, r, i) => s + (i === idx ? 0 : (parseFloat(r.amount) || 0)), 0);
    const remainder = Math.max(0, +(grandTotal - others).toFixed(2));
    updateSplit(idx, 'amount', String(remainder));
  };

  const updateItem = (idx, key, val) => {
    setItems(prev => prev.map((it, i) => {
      if (i !== idx) return it;
      const next = { ...it, [key]: val };
      if (key === 'service_id') {
        const svc = services.find(s => s.id === val);
        if (svc) { next.service_name = svc.name; next.base_amount = String(svc.price || 0); next.tax_rate = Number(svc.tax_rate || 0); next.description = svc.name; }
      }
      if (key === 'doctor_id') {
        const doc = doctors.find(d => d.id === val);
        if (doc) next.doctor_name = `${doc.first_name} ${doc.last_name}`.trim();
      }
      return next;
    }));
  };
  const addItem = () => setItems(prev => [...prev, { doctor_id: '', doctor_name: '', service_id: '', service_name: '', description: '', base_amount: '', tax_rate: 0 }]);
  const removeItem = (idx) => setItems(prev => prev.length === 1 ? prev : prev.filter((_, i) => i !== idx));

  const handleSubmit = async () => {
    if (!selectedPatient) { toast.error('Please select a patient'); return; }
    if (grandTotal <= 0) { toast.error('Please enter a valid amount'); return; }
    if (!splitsMatch) {
      toast.error(`Split totals (Rs. ${splitSum.toFixed(2)}) don't match invoice total (Rs. ${grandTotal.toFixed(2)})`);
      return;
    }
    setSubmitting(true);
    try {
      const cleanSplits = splits
        .filter(s => (parseFloat(s.amount) || 0) > 0)
        .map(s => ({ method: s.method, amount: parseFloat(s.amount), reference: s.reference || '' }));
      // Item 5 — multi-consultation: send `items` array. Backend re-validates sum.
      const cleanItems = multiConsult ? itemsTotals.filter(it => it._amount > 0).map(it => ({
        service_id: it.service_id || null,
        service_name: it.service_name || it.description || null,
        doctor_id: it.doctor_id || null,
        doctor_name: it.doctor_name || null,
        description: it.description || null,
        base_amount: it._base,
        tax_rate: parseFloat(it.tax_rate) || 0,
        tax_amount: it._tax,
        amount: it._amount,
      })) : [];
      const res = await paymentsAPI.create({
        patient_id: selectedPatient.id,
        amount: grandTotal,
        base_amount: base,
        tax_amount: taxAmount,
        payment_method: cleanSplits[0]?.method || 'cash',
        splits: cleanSplits,
        items: cleanItems,
        description: multiConsult ? `Multi-consultation: ${cleanItems.map(i => i.doctor_name || i.service_name).filter(Boolean).join(', ')}` : description,
        service_id: multiConsult ? null : (selectedService?.id || null),
        service_name: multiConsult ? null : (selectedService?.name || null),
      });
      toast.success('Payment recorded successfully');
      onSuccess({ ...res.data, patient: selectedPatient });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record payment');
    }
    setSubmitting(false);
  };

  // Group services by category for nicer dropdown
  const grouped = services.reduce((acc, s) => {
    const k = s.category || 'Other';
    (acc[k] = acc[k] || []).push(s);
    return acc;
  }, {});

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><CreditCard size={18} className="text-teal-600" /> Payment Collected</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* 1. Patient */}
          {!selectedPatient ? (
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Search Patient</label>
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  placeholder="Search by name, UHID, or phone..."
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="pl-9 h-10"
                  data-testid="payment-patient-search"
                  autoFocus
                />
              </div>
              {searchResults.length > 0 && (
                <div className="mt-2 max-h-40 overflow-y-auto border rounded-lg">
                  {searchResults.map(p => (
                    <button key={p.id} onClick={() => { setSelectedPatient(p); setSearchResults([]); setSearchQuery(''); }}
                      className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b last:border-b-0 flex items-center gap-3"
                      data-testid={`payment-patient-${p.id}`}>
                      <User size={14} className="text-slate-400" />
                      <div>
                        <p className="text-xs font-medium text-slate-800">{p.first_name} {p.last_name}</p>
                        <p className="text-[10px] text-slate-400">{p.patient_id} · {p.phone}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-3 p-3 bg-teal-50 rounded-lg">
              <User size={16} className="text-teal-600" />
              <div className="flex-1">
                <p className="text-sm font-medium text-slate-800">{selectedPatient.first_name} {selectedPatient.last_name}</p>
                <p className="text-[10px] text-slate-500">{selectedPatient.patient_id} · {selectedPatient.phone}</p>
              </div>
              <button onClick={() => setSelectedPatient(null)} className="text-xs text-slate-400 hover:text-red-500" data-testid="change-patient-btn">Change</button>
            </div>
          )}

          {/* Item 5 — Multi-consultation toggle */}
          <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-lg p-3">
            <div>
              <p className="text-sm font-medium text-slate-700">Multi-Consultation Invoice</p>
              <p className="text-[11px] text-slate-500">Bill multiple doctors/services on one receipt &amp; single payment.</p>
            </div>
            <button
              type="button"
              onClick={() => setMultiConsult(v => !v)}
              className={cn('relative inline-flex h-6 w-11 items-center rounded-full transition-colors', multiConsult ? 'bg-teal-600' : 'bg-slate-300')}
              data-testid="multi-consult-toggle"
            >
              <span className={cn('inline-block h-5 w-5 transform rounded-full bg-white transition-transform', multiConsult ? 'translate-x-5' : 'translate-x-0.5')} />
            </button>
          </div>

          {multiConsult ? (
            /* Item 5 — Multi-consultation line repeater */
            <div className="space-y-2" data-testid="multi-consult-items">
              <div className="flex items-center justify-between">
                <label className="text-xs font-medium text-slate-600">Consultation Lines</label>
                <button onClick={addItem} className="text-xs text-teal-600 hover:text-teal-700 flex items-center gap-1" data-testid="add-item-btn">
                  <Plus size={12} /> Add consultation
                </button>
              </div>
              {itemsTotals.map((it, idx) => (
                <div key={idx} className="border border-slate-200 rounded-lg p-3 bg-white space-y-2" data-testid={`item-row-${idx}`}>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-[10px] text-slate-500">Doctor</Label>
                      <Select value={it.doctor_id} onValueChange={v => updateItem(idx, 'doctor_id', v)}>
                        <SelectTrigger className="h-9" data-testid={`item-doctor-${idx}`}><SelectValue placeholder="Pick doctor" /></SelectTrigger>
                        <SelectContent className="max-h-60">
                          {doctors.map(d => <SelectItem key={d.id} value={d.id}>{d.first_name} {d.last_name}{d.specialization ? ` · ${d.specialization}` : ''}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-[10px] text-slate-500">Service (optional)</Label>
                      <Select value={it.service_id} onValueChange={v => updateItem(idx, 'service_id', v)}>
                        <SelectTrigger className="h-9" data-testid={`item-service-${idx}`}><SelectValue placeholder="Catalog or custom" /></SelectTrigger>
                        <SelectContent className="max-h-60">
                          {services.map(s => <SelectItem key={s.id} value={s.id}>{s.name} — Rs. {Number(s.price || 0).toLocaleString()}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <Input value={it.description} onChange={e => updateItem(idx, 'description', e.target.value)} placeholder="Description (e.g., New consultation)" className="h-9" data-testid={`item-desc-${idx}`} />
                  <div className="grid grid-cols-3 gap-2 items-end">
                    <div>
                      <Label className="text-[10px] text-slate-500">Base (Rs.)</Label>
                      <Input type="number" min="0" step="0.01" value={it.base_amount} onChange={e => updateItem(idx, 'base_amount', e.target.value)} className="h-9" data-testid={`item-base-${idx}`} />
                    </div>
                    <div>
                      <Label className="text-[10px] text-slate-500">Tax %</Label>
                      <Input type="number" min="0" max="100" step="0.5" value={it.tax_rate} onChange={e => updateItem(idx, 'tax_rate', e.target.value)} className="h-9" data-testid={`item-tax-${idx}`} />
                    </div>
                    <div className="flex items-center gap-2 pb-1">
                      <span className="text-xs text-slate-500">Line total:</span>
                      <span className="text-sm font-semibold text-teal-700 tabular-nums">Rs. {it._amount.toFixed(2)}</span>
                      {items.length > 1 && (
                        <button onClick={() => removeItem(idx)} className="ml-auto text-red-400 hover:text-red-600" data-testid={`item-remove-${idx}`}><X size={14} /></button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <>
              {/* 2. Service / Fee Category */}
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Fee Category (Service Catalog)</label>
                <Select value={selectedServiceId} onValueChange={handleServiceChange}>
                  <SelectTrigger className="h-10" data-testid="payment-service-select">
                    <SelectValue placeholder={services.length ? 'Select a service or choose custom' : 'No services configured — using custom amount'} />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    <SelectItem value="__custom__">— Custom (enter manually) —</SelectItem>
                    {Object.keys(grouped).sort().map(cat => (
                      <div key={cat}>
                        <div className="px-2 py-1.5 text-[10px] uppercase tracking-wide text-slate-400 font-semibold">{cat}</div>
                        {grouped[cat].map(s => (
                          <SelectItem key={s.id} value={s.id} data-testid={`service-opt-${s.id}`}>
                            {s.name} — Rs. {Number(s.price || 0).toLocaleString()}{s.tax_rate ? ` (+${s.tax_rate}% tax)` : ''}
                          </SelectItem>
                        ))}
                      </div>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* 3. Description */}
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Description</label>
                <Input
                  placeholder="e.g., Consultation fee, Lab charges..."
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  className="h-10"
                  data-testid="payment-description"
                />
              </div>

              {/* 4. Base Amount + Tax */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1 block">Base Amount (Rs.)</label>
                  <Input
                    type="number"
                    placeholder="0.00"
                    value={baseAmount}
                    onChange={e => setBaseAmount(e.target.value)}
                    className="h-10"
                data-testid="payment-base-amount"
                min="0"
                step="0.01"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Tax %</label>
              <Input
                type="number"
                placeholder="0"
                value={taxRate}
                onChange={e => setTaxRate(parseFloat(e.target.value) || 0)}
                className="h-10"
                data-testid="payment-tax-rate"
                min="0"
                max="100"
                step="0.5"
              />
            </div>
          </div>
            </>
          )}

          {/* Totals summary */}
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-xs space-y-1">
            <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span className="font-medium tabular-nums">Rs. {base.toFixed(2)}</span></div>
            {taxAmount > 0 && (
              <div className="flex justify-between"><span className="text-slate-500">Tax ({taxRate}%)</span><span className="font-medium tabular-nums">Rs. {taxAmount.toFixed(2)}</span></div>
            )}
            <div className="flex justify-between border-t border-slate-200 pt-1 mt-1">
              <span className="text-slate-700 font-semibold">Grand Total</span>
              <span className="text-teal-700 font-bold tabular-nums" data-testid="grand-total">Rs. {grandTotal.toFixed(2)}</span>
            </div>
          </div>

          {/* 5. Splits */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-xs font-medium text-slate-600">Payment Method(s)</label>
              <button onClick={addSplit} className="text-xs text-teal-600 hover:text-teal-700 flex items-center gap-1" data-testid="add-split-btn">
                <Plus size={12} /> Add another method
              </button>
            </div>
            <div className="space-y-2">
              {splits.map((s, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center">
                  <div className="col-span-3">
                    <Select value={s.method} onValueChange={(v) => updateSplit(i, 'method', v)}>
                      <SelectTrigger className="h-9" data-testid={`split-method-${i}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {METHOD_OPTIONS.map(m => (
                          <SelectItem key={m.v} value={m.v}>{m.l}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-3">
                    <Input
                      type="number"
                      placeholder="Amount"
                      value={s.amount}
                      onChange={e => updateSplit(i, 'amount', e.target.value)}
                      className="h-9"
                      data-testid={`split-amount-${i}`}
                      min="0"
                      step="0.01"
                    />
                  </div>
                  <div className="col-span-4">
                    <Input
                      placeholder="Txn / Ref / Cheque #"
                      value={s.reference}
                      onChange={e => updateSplit(i, 'reference', e.target.value)}
                      className="h-9"
                      data-testid={`split-ref-${i}`}
                    />
                  </div>
                  <div className="col-span-2 flex items-center gap-1">
                    <button
                      onClick={() => autoFillRemainder(i)}
                      className="text-[10px] text-slate-500 hover:text-teal-600 px-1.5 py-1 rounded border border-slate-200 hover:border-teal-300"
                      title="Auto-fill remaining amount"
                      data-testid={`split-fill-${i}`}>
                      Fill
                    </button>
                    {splits.length > 1 && (
                      <button onClick={() => removeSplit(i)} className="text-slate-400 hover:text-red-500" data-testid={`split-remove-${i}`}>
                        <X size={14} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Split validation banner */}
            {grandTotal > 0 && (
              <div className={cn('mt-2 flex items-center gap-2 text-xs px-3 py-2 rounded-md',
                splitsMatch ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'
              )}>
                {splitsMatch ? <CheckCircle2 size={12} /> : <AlertCircle size={12} />}
                <span>
                  Split sum: <span className="font-semibold tabular-nums">Rs. {splitSum.toFixed(2)}</span> / <span className="tabular-nums">Rs. {grandTotal.toFixed(2)}</span>
                  {splitsMatch ? ' — matches grand total' : ` — short by Rs. ${(grandTotal - splitSum).toFixed(2)}`}
                </span>
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting || !selectedPatient || grandTotal <= 0 || !splitsMatch}
            className="bg-teal-600 hover:bg-teal-700 gap-2" data-testid="submit-payment-btn">
            {submitting ? 'Recording...' : (<><CheckCircle2 size={14} /> Confirm Payment</>)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ===================== Receipt Print Dialog =====================
const ReceiptPrintDialog = ({ receipt, hospital, onClose }) => {
  const handlePrint = async () => {
    let patient = receipt.patient || {};
    // Fetch full patient detail for richer receipt (address, DOB, VIP)
    try {
      const r = await patientsAPI.get(receipt.patient_id || patient.id);
      patient = r.data || patient;
    } catch { /* keep light-weight selected patient */ }
    const ok = printReceiptHTML({ receipt, patient, hospital });
    if (!ok) toast.error('Pop-up blocked. Please allow pop-ups to print.');
  };

  const p = receipt.patient || {};
  const splits = Array.isArray(receipt.splits) && receipt.splits.length
    ? receipt.splits
    : [{ method: receipt.payment_method || 'cash', amount: receipt.amount }];

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="receipt-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-emerald-700">
            <CheckCircle2 size={18} /> Payment Recorded
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
            <p className="text-[11px] uppercase tracking-wider text-emerald-700 font-semibold">Receipt</p>
            <p className="text-xl font-bold text-slate-900 mt-1">{receipt.receipt_number}</p>
            <p className="text-xs text-slate-600 mt-2">{p.first_name} {p.last_name} · {p.patient_id}</p>
            <p className="text-2xl font-bold text-emerald-700 mt-2 flex items-center"><IndianRupee size={20} />{Number(receipt.amount || 0).toLocaleString('en-IN')}</p>
            <p className="text-[11px] text-slate-500 mt-1">{receipt.service_name || receipt.description || 'Hospital service charge'}</p>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {splits.map((s, i) => (
                <span key={i} className="text-[10px] bg-white border border-emerald-300 text-emerald-700 rounded-full px-2 py-0.5 font-medium">
                  {(s.method || '').toUpperCase()} · Rs. {Number(s.amount || 0).toLocaleString('en-IN')}
                </span>
              ))}
            </div>
          </div>
          <p className="text-xs text-slate-500 text-center">Generate and print the branded invoice?</p>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} data-testid="receipt-skip-btn">Skip</Button>
          <Button onClick={handlePrint} className="bg-teal-600 hover:bg-teal-700 gap-2" data-testid="receipt-print-btn">
            <Printer size={14} /> Generate & Print Invoice
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentsPage;
